import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  useZendeskIntegration,
  useIsZendeskEnabled,
  useZendeskPreviewEnabled,
} from "../../shared/store/portalSchemaStore";
import { useEnduserIsAuthenticated, useEnduserCurrentEnduser } from "../store/hooks";
import { ZENDESK_SNIPPET_KEY_PATTERN } from "../../shared/schema";
import { formatName } from "@tellescope/utils";
import { useZendeskAuth } from "../hooks/useZendeskAuth";

declare global {
  interface Window {
    zE?: (namespace: "webWidget" | "messenger", command: string, data?: unknown) => void;
  }
}

const ZENDESK_SCRIPT_BASE_URL = "https://static.zdassets.com/ekr/snippet.js";

const zeCall = (widgetVersion: "classic" | "messaging", command: string, data?: unknown) => {
  const namespace = widgetVersion === "classic" ? "webWidget" : "messenger";
  try {
    window.zE?.(namespace, command, data);
  } catch (err) {
    // Zendesk's runtime can throw if a command is called before the widget has fully
    // initialized (e.g. show/hide right after the snippet script's onload, before the
    // Messenger API surface is registered) — never let a third-party widget crash the app.
    console.error(`ZendeskChatWidget: zE("${namespace}", "${command}") failed`, err);
  }
};

export const ZendeskChatWidget: React.FC = () => {
  const location = useLocation();
  const isEnabled = useIsZendeskEnabled();
  const config = useZendeskIntegration();
  const isPreviewEnabled = useZendeskPreviewEnabled();
  const isAuthenticated = useEnduserIsAuthenticated();
  const currentEnduser = useEnduserCurrentEnduser();

  const isAdminRoute = location.pathname.startsWith("/admin");
  const shouldShow = isEnabled && (isPreviewEnabled || (!isAdminRoute && isAuthenticated));
  const shouldShowRef = useRef(shouldShow);
  shouldShowRef.current = shouldShow;

  const fullName = currentEnduser ? formatName(currentEnduser) || undefined : undefined;
  const userEmail = currentEnduser?.email;
  const userPhone = currentEnduser?.phone;

  // Refs so onload closure always reads the latest enduser data without creating dependencies.
  const fullNameRef = useRef(fullName);
  fullNameRef.current = fullName;
  const userEmailRef = useRef(userEmail);
  userEmailRef.current = userEmail;
  const userPhoneRef = useRef(userPhone);
  userPhoneRef.current = userPhone;

  const widgetVersion = config?.widgetVersion;
  const jwt = useZendeskAuth(widgetVersion);

  // Identity passing is Classic-only. ZenDesk Messaging identifies users via a signed JWT
  // (loginUser command) — there is no unauthenticated identify API. To support this for
  // Messaging, a server-side JWT endpoint would need to be wired up via the V2 authEnabled/
  // jwtEndpoint schema fields, and the loginUser call made here after script load.
  const identifyEnduser = (version: "classic" | "messaging") => {
    if (version !== "classic") return;
    const name = fullNameRef.current;
    const email = userEmailRef.current;
    const phone = userPhoneRef.current;
    if (name || email) zeCall(version, "identify", { name, email });
    if (name || email || phone) zeCall(version, "prefill", { name, email, phone });
  };

  // Inject the script once when config is valid, regardless of auth state.
  // The widget starts hidden; the show/hide and identify effects run after zE is ready.
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (!config?.snippetKey || !ZENDESK_SNIPPET_KEY_PATTERN.test(config.snippetKey)) {
      console.error("ZendeskChatWidget: Invalid or missing snippet key");
      return;
    }

    if (!widgetVersion) {
      console.error("ZendeskChatWidget: Widget version must be specified");
      return;
    }

    if (document.getElementById("ze-snippet")) {
      return;
    }

    const script = document.createElement("script");
    script.id = "ze-snippet";
    script.src = `${ZENDESK_SCRIPT_BASE_URL}?key=${config.snippetKey}`;
    script.async = true;

    const resolvedVersion = widgetVersion;
    script.onload = () => {
      // The show/hide and identify effects fired before the script loaded (window.zE was
      // undefined so they were no-ops). Apply the correct state now that zE is ready.
      zeCall(resolvedVersion, shouldShowRef.current ? "show" : "hide");
      if (shouldShowRef.current) {
        identifyEnduser(resolvedVersion);
      }
    };

    document.body.appendChild(script);

    return () => {
      document.getElementById("ze-snippet")?.remove();
      if (typeof window !== "undefined") {
        delete window.zE;
      }
    };
  }, [isEnabled, config, widgetVersion]);

  // Re-identify and re-show when enduser data changes after initial load (e.g. profile update).
  useEffect(() => {
    if (!shouldShow || !widgetVersion || !window.zE) {
      return;
    }
    identifyEnduser(widgetVersion);
  }, [shouldShow, widgetVersion, fullName, userEmail, userPhone]);

  // Show or hide the widget launcher based on auth state.
  // ZenDesk's runtime cannot be unloaded once started, so hide/show is the only lever.
  useEffect(() => {
    if (!widgetVersion || !window.zE) {
      return;
    }
    zeCall(widgetVersion, shouldShow ? "show" : "hide");
  }, [shouldShow, widgetVersion]);

  // Authenticate the Messaging session once a JWT is available from the business's
  // signing endpoint (see useZendeskAuth). Non-blocking — the widget is already shown
  // unauthenticated; this upgrades it in place. No fallback identify path exists for
  // Messaging, so if this never fires the widget simply stays unauthenticated.
  useEffect(() => {
    if (widgetVersion !== "messaging" || !jwt || !window.zE) {
      return;
    }
    zeCall(widgetVersion, "loginUser", (callback: (token: string) => void) => callback(jwt));
  }, [widgetVersion, jwt]);

  return null;
};
